import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { Telemetry } from '~src/commonInfra/services/Telemetry';
import { TelemetryEvents, VPCFeatureUsageEvent, VPCFeatureUsageType } from '~src/commonInfra/telemetryEvents/TelemetryEvents';
import { SliceableHelper } from '~src/httpClient/utils/SliceableHelper';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { isProjectSpace, isSiteSpace, SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';

import { DestinationProjectContentType, DestinationProjects } from '../resources/DestinationProjects';
import { IDestinationProject } from '../ServerApi/types/IDestinationProject';

export const minWindowSizeToEnableProjectTree = 1200;

export const LocalStorageKeyPrefix = 'ProjectTreeToggleState';
export type ToggleState = 'on' | 'off';
export const projectTreeToggleFeatureName = 'ProjectTreeToggle';
export const projectTreeFeatureName = 'ProjectTree';
export const resizableProjectTreeFeatureName = 'ResizableProjectTree';

// Resizable Project Tree ================================
export const LocalStorageWidthKey = 'ProjectTreeResizableWidth';
export const MinTreeWidth = 320;
export const MaxTreeViewportWidthFactor = 0.4;
export const KeyboardStep = 8;
export const KeyboardStepFast = 32;
// =======================================================

export class ProjectTreeService {
    public static instance = new ProjectTreeService();

    private searchBarRef: React.RefObject<HTMLDivElement>;

    private treeNodeExpandedValues: Set<string>;

    public toggleState: ToggleState = 'off';

    public initialize() {
        const state = localStorage.getItem(this.getToggleStateLocalStorageKey()) as ToggleState;
        if (state === 'on' || state === 'off') {
            this.toggleState = state;
        } else {
            this.toggleState = 'on';
        }
        this.triggerOnProjectTreeToggleStateChanged();
    }

    public onProjectTreeToggleStateChanged: ((toggleState: ToggleState) => void)[] = [];

    public async getAllProjects(): Promise<IDestinationProject[]> {
        const contentType = 'project' as DestinationProjectContentType;
        const projectsQuery = DestinationProjects.getQueryForSpecificSourceContentAndMatchText(contentType, [], '');
        const result = await SliceableHelper.fetchAllConcurrent(projectsQuery);
        return result.items;
    }

    public isProjectTreeEnabled(spaceInfo: SpaceInfo) {
        return FeatureFlags.projectTreeEnabled && (isSiteSpace(spaceInfo) || isProjectSpace(spaceInfo));
    }

    public toggle() {
        this.toggleState = this.toggleState === 'on' ? 'off' : 'on';
        localStorage.setItem(this.getToggleStateLocalStorageKey(), this.toggleState);
        this.triggerOnProjectTreeToggleStateChanged();

        this.sendProjectTreeToggleUsageTelemetryEvent('click_through');
    }

    public sendProjectTreeToggleUsageTelemetryEvent(usageType: VPCFeatureUsageType) {
        Telemetry.sendEvent<VPCFeatureUsageEvent>({
            type: TelemetryEvents.VPCFeatureUsage,
            feature_name: projectTreeToggleFeatureName,
            usage_type: usageType,
            extra_parameter_a: this.toggleState
        });
    }

    public sendProjectTreeUsageTelemetryEvent(usageType: VPCFeatureUsageType) {
        Telemetry.sendEvent<VPCFeatureUsageEvent>({
            type: TelemetryEvents.VPCFeatureUsage,
            feature_name: projectTreeFeatureName,
            usage_type: usageType
        });
    }

    public saveTreeNodeExpandedValues(values: Set<string>) {
        this.treeNodeExpandedValues = new Set(values);
    }

    public getTreeNodeExpandedValues(): Set<string> {
        return this.treeNodeExpandedValues;
    }

    private triggerOnProjectTreeToggleStateChanged() {
        this.onProjectTreeToggleStateChanged.forEach(handler => {
            handler(this.toggleState);
        });
    }

    private getToggleStateLocalStorageKey() {
        const sessionManagementService = getSessionManagementServiceInstance();
        return LocalStorageKeyPrefix + '-' + sessionManagementService.sessionUserLuid();
    }

    public setSearchBarRef = (searchBarRef: React.RefObject<HTMLDivElement>) => {
        this.searchBarRef = searchBarRef;
    };

    public useSearchBarRef() {
        return this.searchBarRef;
    }

    // Resizable Project Tree ================================
    public isResizableProjectTreeEnabled() {
        return FeatureFlags.resizableProjectTreeEnabled;
    }

    public loadStoredTreeWidth(): number {
        try {
            const value = window.localStorage.getItem(LocalStorageWidthKey);
            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : MinTreeWidth;
        }
        catch {
            return MinTreeWidth;
        }
    }

    public persistTreeWidth(width: number): void {
        try {
            window.localStorage.setItem(LocalStorageWidthKey, String(Math.round(width)));
        }
        catch {
            // ignore persistence errors (e.g. storage disabled)
        }
    }
    // =======================================================
}